import iso3166 from "iso-3166-1"
import { mapAPI } from "../api/csr/map"
import {
	formatKpiScore,
	getKpiScoreTier,
} from "./supplierKpiOverall"

export const MAP_TIER_COLORS = {
	green: "#2e7d32",
	yellow: "#f9a825",
	red: "#c62828",
	default: "#ececec",
}

function resolveCountryMapId(country) {
	if (!country) {
		return null
	}

	const trimmed = String(country).trim()
	if (!trimmed || trimmed === "Undefined") {
		return null
	}

	let iso = iso3166.whereCountry(trimmed)
	if (iso?.alpha2) {
		return iso.alpha2.toLowerCase()
	}

	if (/^[a-z]{2}$/i.test(trimmed)) {
		iso = iso3166.whereAlpha2(trimmed.toUpperCase())
		if (iso?.alpha2) {
			return iso.alpha2.toLowerCase()
		}
		return trimmed.toLowerCase()
	}

	if (/^[a-z]{3}$/i.test(trimmed)) {
		iso = iso3166.whereAlpha3(trimmed.toUpperCase())
		if (iso?.alpha2) {
			return iso.alpha2.toLowerCase()
		}
	}

	return null
}

export function buildSupplierPerformanceMapCountries(
	countryArray,
	companyArray,
	rawKpiScores,
	breakdownLabels = [],
	clientIds = [],
) {
	const byMapId = {}
	const suppliers = []

	for (let i = 0; i < countryArray.length; i++) {
		const country = countryArray[i]
		const rawScore = rawKpiScores[i]
		const company = companyArray[i]
		const label = breakdownLabels[i] ?? formatKpiScore(rawScore)
		const mapId = country && country !== "Undefined"
			? resolveCountryMapId(country)
			: null
		const tier = getKpiScoreTier(rawScore)

		suppliers.push({
			clientID: clientIds[i] ?? null,
			name: company,
			country: country && country !== "Undefined" ? country : "Unknown country",
			mapId,
			score: label,
			tier,
			fill: tier ? MAP_TIER_COLORS[tier] : MAP_TIER_COLORS.default,
		})

		if (!country || country === "Undefined" || !mapId) {
			continue
		}

		if (!byMapId[mapId]) {
			byMapId[mapId] = {
				id: mapId,
				name: country,
				total: 0,
				count: 0,
				suppliers: [],
			}
		}

		const entry = byMapId[mapId]
		if (rawScore !== null && rawScore !== undefined && rawScore !== "") {
			const numeric = Number(rawScore)
			if (Number.isFinite(numeric)) {
				entry.total += numeric
				entry.count += 1
			}
		}
		entry.suppliers.push({
			name: company,
			score: label,
			clientID: clientIds[i] ?? null,
			rawScore:
				rawScore !== null &&
				rawScore !== undefined &&
				rawScore !== "" &&
				Number.isFinite(Number(rawScore))
					? Number(rawScore)
					: null,
			tier,
		})
	}

	const countries = Object.values(byMapId).map((entry) => {
		const averageKpi = entry.count > 0 ? entry.total / entry.count : null
		const tier = getKpiScoreTier(averageKpi)
		const scoredSuppliers = entry.suppliers.filter(
			(supplier) => supplier.tier && supplier.rawScore !== null,
		)

		let fillMode = "solid"
		let splitSlices = null

		if (scoredSuppliers.length >= 2) {
			fillMode = "split"
			splitSlices = [...scoredSuppliers]
				.sort((a, b) => a.rawScore - b.rawScore)
				.map((supplier) => ({
					name: supplier.name,
					score: supplier.score,
					fill: MAP_TIER_COLORS[supplier.tier],
				}))
		}

		return {
			id: entry.id,
			name: entry.name,
			averageKpi,
			averageLabel:
				averageKpi === null ? "No KPI data" : formatKpiScore(averageKpi),
			tier,
			fillMode,
			splitSlices,
			fill:
				fillMode === "split"
					? splitSlices[0].fill
					: tier
						? MAP_TIER_COLORS[tier]
						: MAP_TIER_COLORS.default,
			suppliers: entry.suppliers.map(({ name, score, clientID }) => ({
				name,
				score,
				clientID,
			})),
		}
	})

	return { countries, suppliers }
}

export function aggregateConnectionMapData(countryArray, companyArray, scoresArray, breakdownLabels = []) {
	const cumulativeScores = {}
	const uniqueCountryArray = []
	const uniqueCodesArray = []
	const cumulativeScoresArray = []
	const breakdownArray = []

	for (let i = 0; i < countryArray.length; i++) {
		const country = countryArray[i]
		const score = scoresArray[i]
		const company = companyArray[i]
		const label = breakdownLabels[i] ?? score
		const code = iso3166.whereCountry(country)?.alpha3 || "Unknown"

		if (!cumulativeScores[country]) {
			cumulativeScores[country] = {
				total: 0,
				count: 0,
				breakdown: [],
			}
			uniqueCountryArray.push(country)
			uniqueCodesArray.push(code)
		}

		cumulativeScores[country].total += score
		cumulativeScores[country].count += 1
		cumulativeScores[country].breakdown.push(`${company} - ${label}`)
	}

	for (const country of uniqueCountryArray) {
		const averageScore = cumulativeScores[country].total / cumulativeScores[country].count
		cumulativeScoresArray.push(averageScore)
		breakdownArray.push(cumulativeScores[country].breakdown.join("<br>"))
	}

	return {
		uniqueCountryArray,
		uniqueCodesArray,
		cumulativeScoresArray,
		breakdownArray,
	}
}

export async function publishConnectionMapChart({
	uniqueCountryArray,
	uniqueCodesArray,
	cumulativeScoresArray,
	breakdownArray,
	chartId,
}) {
	await mapAPI.updateChart(
		uniqueCountryArray,
		uniqueCodesArray,
		cumulativeScoresArray,
		breakdownArray,
		chartId,
	)
	return mapAPI.publishMap(chartId)
}
